import { create } from "zustand";
import { persist } from "zustand/middleware";
import { TOKEN_KEY, REFRESH_KEY } from "@/api/client";
import type { AuthUser, KeycloakTokenResponse, OidcUserInfo, UserRole } from "@/types";

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Extract the first matching app role from Keycloak claims.
 *  Keycloak's /userinfo endpoint returns roles as a flat top-level array.
 *  The JWT itself nests them under realm_access.roles.
 *  We check both so the same function works in either context.
 */
function extractRole(info: OidcUserInfo, tokenRoles: string[] = []): UserRole {
  // 1. Flat top-level roles[] — what /userinfo returns *if* a mapper adds it
  const flat = info.roles ?? [];
  // 2. Nested realm_access.roles — present when userinfo includes it
  const nested = info.realm_access?.roles ?? [];
  // 3. Roles decoded straight from the access-token JWT. Keycloak's /userinfo
  //    endpoint does NOT include realm roles by default, but the access token
  //    always carries realm_access.roles — so this is the reliable source.
  const all = [...flat, ...nested, ...tokenRoles];

  if (all.includes("SuperAdmin")) return "SuperAdmin";
  if (all.includes("Teacher"))    return "Teacher";
  return "Student";
}

/** Base64URL-decode a JWT payload segment. Returns null on any malformation. */
function decodeJwtPayload(token?: string | null): Record<string, unknown> | null {
  try {
    if (!token) return null;
    const payload = token.split(".")[1];
    if (!payload) return null;
    return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
  } catch {
    return null;
  }
}

/** Extract realm_access.roles from an access-token JWT. */
export function rolesFromToken(token?: string | null): string[] {
  const payload = decodeJwtPayload(token) as
    | { realm_access?: { roles?: string[] } }
    | null;
  return payload?.realm_access?.roles ?? [];
}

/**
 * Returns true if the JWT stored in localStorage is still valid
 * (i.e. the `exp` claim is in the future with a 30-second buffer).
 * Returns false if the token is missing, malformed, or expired.
 */
export function isTokenValid(): boolean {
  try {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return false;
    // JWT is three Base64URL segments separated by "."
    const payload = token.split(".")[1];
    if (!payload) return false;
    const { exp } = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/"))) as { exp?: number };
    if (!exp) return false;
    // 30-second buffer so we don't use a token that expires mid-request
    return Date.now() < (exp - 30) * 1000;
  } catch {
    return false;
  }
}

/** Map OIDC userinfo claims → normalized AuthUser stored in Zustand.
 *  Pass the access token so roles can be read from the JWT — the /userinfo
 *  response usually omits realm roles. */
export function normalizeUser(info: OidcUserInfo, accessToken?: string | null): AuthUser {
  return {
    id:         info.sub,
    username:   info.preferred_username,
    email:      info.email,
    firstName:  info.given_name,
    lastName:   info.family_name,
    role:       extractRole(info, rolesFromToken(accessToken)),
    avatarUrl:  info.picture,
  };
}

// ─── Store ────────────────────────────────────────────────────────────────────

interface AuthState {
  user:            AuthUser | null;
  accessToken:     string | null;
  refreshToken:    string | null;
  isAuthenticated: boolean;

  /** Call after a successful login/refresh — persists tokens + user */
  setSession: (tokens: KeycloakTokenResponse, user: AuthUser) => void;

  /** Clear all auth state (logout / 401 unrecoverable) */
  clearSession: () => void;

  /** Partial update when user profile changes */
  updateUser: (partial: Partial<AuthUser>) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user:            null,
      accessToken:     null,
      refreshToken:    null,
      isAuthenticated: false,

      setSession: (tokens, user) => {
        // Keep tokens in localStorage so the Axios interceptor can read them
        localStorage.setItem(TOKEN_KEY,   tokens.access_token);
        localStorage.setItem(REFRESH_KEY, tokens.refresh_token);
        set({
          user,
          accessToken:     tokens.access_token,
          refreshToken:    tokens.refresh_token,
          isAuthenticated: true,
        });
      },

      clearSession: () => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_KEY);
        set({ user: null, accessToken: null, refreshToken: null, isAuthenticated: false });
      },

      updateUser: (partial) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...partial } : null,
        })),
    }),
    {
      name: "orion-auth",
      // Only persist non-sensitive user metadata in sessionStorage
      // Actual tokens live in localStorage (managed above)
      partialize: (state) => ({
        user:            state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
